import { useEffect, useRef, useState } from 'preact/hooks'
import { ajusterGraphe } from './graphe-editeur.ts'
import { SallesEditor } from './salles.tsx'
import { LiensSallesInventaire } from './liens-salles-inventaire.tsx'
import type { ComponentChildren } from 'preact'
import {
  ParcoursEditor,
  parcoursInitial,
  NOMS_BLOCS,
  creerBloc,
} from './parcours.tsx'
import type { FicheEvenement } from '../../serveur/src/evenements/index.ts'

import { apiBibliotheque } from './formulaires.tsx'
import {
  type ItemInventaire,
  type DonneesInventaire,
  prixInventaire,
  UNITES_INVENTAIRE,
} from './inventaire-types.ts'

interface Detail {
  cle: string
  version_id: string
  version: number
  fiche: FicheEvenement
  etat: string
  a_un_brouillon: boolean
  apercu_a_jour: boolean
  places_prises: number
  paiement_en_demonstration: boolean
  url: string
  manques: Array<{ message?: string; gravite?: string }>
  avance: boolean
  url_apercu?: string
}
interface Props {
  baseUrl: string
  permissions: string[]
  onAuth(): void
  onChange(): void
  onEditionChange?(edition: boolean): void
  intentionCreation?: number
  intentionOuverture?: number
  selectionInitiale?: string
  visible?: boolean
  articleInitial?: ItemInventaire
  onInventaire?(id: string): void
}
const JOURS = [
  { cle: 'lundi', nom: 'Lun' },
  { cle: 'mardi', nom: 'Mar' },
  { cle: 'mercredi', nom: 'Mer' },
  { cle: 'jeudi', nom: 'Jeu' },
  { cle: 'vendredi', nom: 'Ven' },
  { cle: 'samedi', nom: 'Sam' },
  { cle: 'dimanche', nom: 'Dim' },
] as const
const ETAPES = ['Élément et offre', 'Disponibilités', 'Parcours', 'Publication']
const prix = (cents: number) =>
  new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(cents / 100)
const dateLisible = (date: string) =>
  date && Number.isFinite(Date.parse(date))
    ? new Intl.DateTimeFormat('fr-CA', {
        dateStyle: 'long',
        timeZone: 'UTC',
      }).format(new Date(`${date}T12:00:00Z`))
    : 'Date à choisir'
const dateFuture = () =>
  new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
const nouvelleFiche = (): FicheEvenement => {
  const fiche: FicheEvenement = {
    type: 'demande',
    nom: '',
    description: '',
    lieu: '',
    adresse: '',
    date: dateFuture(),
    debut: '18:00',
    fin: '20:00',
    capacite: 30,
    maximum_par_inscription: 1,
    ressources: [{ nom: 'Salle principale' }],
    jours: [
      'lundi',
      'mardi',
      'mercredi',
      'jeudi',
      'vendredi',
      'samedi',
      'dimanche',
    ],
    ouverture: '08:00',
    fermeture: '22:00',
    duree_minimum: 60,
    duree_maximum: 240,
    pas_minutes: 30,
    horizon_jours: 90,
    prix_cents: 0,
    depot_cents: 0,
    telephone: false,
    approbation: false,
    contrat: '',
    questions: [],
  }
  return { ...fiche, parcours: parcoursInitial(fiche) }
}
function ficheDepuisArticle(
  item: ItemInventaire,
  precedente = nouvelleFiche(),
  conserver = false,
): FicheEvenement {
  const f: FicheEvenement = {
    ...precedente,
    ...item.fiche.disponibilites,
    type:
      item.fiche.nature === 'achat'
        ? 'achat'
        : item.fiche.type === 'activite'
          ? 'evenement'
          : 'location',
    inventaire_item_id: item.id,
    subdivisions: [],
    compte_gl: item.fiche.compte_gl,
    nom: conserver ? precedente.nom : item.fiche.nom,
    description: conserver ? precedente.description : item.fiche.description,
    lieu:
      precedente.lieu || (item.fiche.type === 'espace' ? item.fiche.nom : ''),
    prix_cents: item.fiche.prix_cents,
    capacite: item.fiche.quantite,
    ressources: item.ressource_id
      ? [{ cle: item.fiche.ressource_cle, nom: item.fiche.nom }]
      : [],
  }
  let blocs = structuredClone(precedente.parcours ?? [])
  const inserer = (type: Parameters<typeof creerBloc>[0], avant: string[]) => {
    if (blocs.some((b) => b.bloc === type)) return
    const index = blocs.findIndex((b) => avant.includes(b.bloc))
    blocs.splice(index < 0 ? blocs.length : index, 0, creerBloc(type, f))
  }
  if (f.type === 'achat') {
    f.depot_cents = 0
    blocs = blocs.filter((b) => !['creneau', 'depot'].includes(b.bloc))
    inserer('offres', ['contrat', 'signature', 'paiement', 'confirmation'])
    const premiere = blocs.find((b) => b.bloc === 'offres')
    if (premiere?.bloc === 'offres')
      premiere.quantite_principale_maximum = Math.min(1000, item.fiche.quantite)
  } else
    inserer('creneau', [
      'offres',
      'contrat',
      'signature',
      'paiement',
      'depot',
      'confirmation',
    ])
  if (f.prix_cents > 0) inserer('paiement', ['confirmation'])
  f.parcours = blocs.map((b) =>
    b.bloc === 'confirmation'
      ? {
          ...b,
          joint_calendrier: f.type !== 'achat',
          message:
            f.type === 'achat'
              ? 'Votre commande est confirmée. Retrouvez les détails dans votre courriel.'
              : 'Votre réservation est confirmée. Retrouvez les détails dans votre courriel.',
        }
      : b.bloc === 'creneau'
        ? {
            ...b,
            choix_de_la_ressource: false,
            quantite_maximum:
              f.type === 'evenement' ? f.maximum_par_inscription : 1,
            periode_fixe: undefined,
          }
        : b,
  )
  if (precedente.graphe_parcours)
    f.graphe_parcours = ajusterGraphe(
      precedente.graphe_parcours,
      precedente.parcours ?? [],
      f.parcours ?? [],
    )
  return f
}
function Duree({
  valeur,
  minimum,
  maximum = 1440,
  pas,
  changer,
}: {
  valeur: number
  minimum: number
  maximum?: number
  pas: number
  changer(v: number): void
}) {
  const [personnalisee, setPersonnalisee] = useState(false)
  const choix = [
    15, 30, 45, 60, 90, 120, 180, 240, 300, 360, 480, 720, 1440,
  ].filter((v) => v >= minimum && v <= maximum && v % pas === 0)
  const libelle = (v: number) =>
    `${Math.floor(v / 60) ? Math.floor(v / 60) + ' h' : ''}${v % 60 ? ' ' + (v % 60) + ' min' : ''}`.trim()
  return (
    <div class="ev-duree">
      <select
        value={personnalisee ? 'autre' : valeur}
        onChange={(e) => {
          if (e.currentTarget.value === 'autre') setPersonnalisee(true)
          else {
            setPersonnalisee(false)
            changer(Number(e.currentTarget.value))
          }
        }}
      >
        {[...new Set([...choix, valeur])]
          .sort((a, b) => a - b)
          .map((v) => (
            <option value={v}>{libelle(v)}</option>
          ))}
        <option value="autre">Durée personnalisée…</option>
      </select>
      {personnalisee ? (
        <input
          aria-label="Durée personnalisée en minutes"
          type="number"
          min={minimum}
          max={maximum}
          step={pas}
          value={valeur}
          onInput={(e) => changer(Number(e.currentTarget.value))}
        />
      ) : null}
    </div>
  )
}
function Champ({
  titre,
  aide,
  children,
}: {
  titre: string
  aide?: string
  children: ComponentChildren
}) {
  return (
    <label class="ev-champ">
      <span>{titre}</span>
      {children}
      {aide ? <small>{aide}</small> : null}
    </label>
  )
}
function Interrupteur({
  titre,
  aide,
  coche,
  changer,
  children,
}: {
  titre: string
  aide?: string
  coche: boolean
  changer(v: boolean): void
  children?: ComponentChildren
}) {
  return (
    <section class="ev-option">
      <label class="ev-switch">
        <span>
          <strong>{titre}</strong>
          {aide ? <small>{aide}</small> : null}
        </span>
        <input
          type="checkbox"
          checked={coche}
          onChange={(e) => changer(e.currentTarget.checked)}
        />
        <span aria-hidden="true" class="ev-switch-dessin" />
      </label>
      {coche && children ? (
        <div class="ev-option-detail">{children}</div>
      ) : null}
    </section>
  )
}
function IconeEvenement({ salle = false }: { salle?: boolean }) {
  return (
    <svg
      class="ev-svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {salle ? (
        <>
          <path d="M4 21V4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v17M2 21h20M9 21V7h7v14M13 13h.01" />
        </>
      ) : (
        <>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M7 3v4m10-4v4M3 10h18M7 14h2m4 0h2m-8 4h2" />
        </>
      )}
    </svg>
  )
}
function Montant({
  valeur,
  changer,
}: {
  valeur: number
  changer(v: number): void
}) {
  const [texte, setTexte] = useState(String(valeur / 100)),
    focus = useRef(false)
  useEffect(() => {
    if (!focus.current) setTexte(String(valeur / 100))
  }, [valeur])
  return (
    <input
      type="number"
      min="0.01"
      max="1000000"
      step="0.01"
      value={texte}
      required
      onFocus={() => {
        focus.current = true
      }}
      onBlur={() => {
        focus.current = false
      }}
      onInput={(e) => {
        const saisie = e.currentTarget.value
        setTexte(saisie)
        changer(Math.round(Number(saisie) * 100))
      }}
    />
  )
}

export function EvenementsPane({
  baseUrl,
  permissions,
  onAuth,
  onChange,
  onEditionChange,
  intentionCreation = 0,
  intentionOuverture = 0,
  selectionInitiale,
  visible = true,
  articleInitial,
  onInventaire,
}: Props) {
  const [articles, setArticles] = useState<ItemInventaire[]>([])
  const [relationsSalles, setRelationsSalles] = useState<DonneesInventaire['relations']>([])
  const modifiable = permissions.includes('services.modifier')
  const [liste, setListe] = useState<Detail[]>([]),
    [fuseau, setFuseau] = useState('America/Toronto'),
    [charge, setCharge] = useState(true)
  const [erreur, setErreur] = useState(''),
    [notice, setNotice] = useState(''),
    [occupation, setOccupation] = useState(false)
  const [vue, setVue] = useState<'liste' | 'editeur'>('liste'),
    [detail, setDetail] = useState<Detail | null>(null),
    [fiche, setFiche] = useState<FicheEvenement>(nouvelleFiche)
  const [etape, setEtape] = useState(0),
    [modifie, setModifie] = useState(false),
    [recherche, setRecherche] = useState(''),
    [filtre, setFiltre] = useState('actifs')
  const [payant, setPayant] = useState(false)
  const [navigation, setNavigation] = useState<{
      type: 'creer' | 'ouvrir' | 'dupliquer'
      cle?: string
    } | null>(null),
    dialogueNavigation = useRef<HTMLDialogElement>(null)
  const [publierOuvert, setPublierOuvert] = useState(false),
    [accord, setAccord] = useState(false),
    [accordDemo, setAccordDemo] = useState(false),
    [aArchiver, setAArchiver] = useState<Detail | null>(null)
  const formulaire = useRef<HTMLFormElement>(null),
    titre = useRef<HTMLHeadingElement>(null),
    dialogue = useRef<HTMLDialogElement>(null),
    chargement = useRef(0)
  const base = baseUrl.replace(/\/$/, '')
  const appeler = async <T,>(
    route: string,
    method = 'GET',
    body?: unknown,
  ): Promise<T> => {
    const reponse = await fetch(`${base}/api/admin/evenements${route}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    const r = await reponse.json().catch(() => ({}))
    if (!reponse.ok) {
      if (reponse.status === 401) onAuth()
      throw new Error(
        r.erreur?.message ??
          'Impossible de terminer cette action. Réessayez dans un instant.',
      )
    }
    return r as T
  }
  const recharger = async () => {
    const n = ++chargement.current
    try {
      const r = await appeler<{ evenements: Detail[]; fuseau: string }>('')
      if (n === chargement.current) {
        setListe(r.evenements)
        setFuseau(r.fuseau)
        setCharge(false)
      }
    } catch (e) {
      if (n === chargement.current) {
        setErreur(String((e as Error).message))
        setCharge(false)
      }
    }
  }
  useEffect(() => {
    if (visible) {
      void recharger()
      void apiBibliotheque<DonneesInventaire>(baseUrl, 'inventaire')
        .then((r) => { setArticles(r.items); setRelationsSalles(r.relations) })
        .catch((e) => {
          if (e.statut === 401) onAuth()
        })
    }
  }, [base, visible])
  useEffect(() => {
    if (intentionCreation > 0 && modifiable) creer()
  }, [intentionCreation])
  useEffect(() => {
    if (selectionInitiale) void ouvrir(selectionInitiale)
  }, [selectionInitiale, intentionOuverture])
  useEffect(() => {
    onEditionChange?.(vue === 'editeur')
  }, [vue])
  useEffect(() => {
    if (
      navigation &&
      dialogueNavigation.current &&
      !dialogueNavigation.current.open
    )
      dialogueNavigation.current.showModal()
  }, [navigation])
  useEffect(() => {
    const prevenir = (e: BeforeUnloadEvent) => {
      if (modifie) e.preventDefault()
    }
    window.addEventListener('beforeunload', prevenir)
    return () => window.removeEventListener('beforeunload', prevenir)
  }, [modifie])
  useEffect(() => {
    if (
      (publierOuvert || aArchiver) &&
      dialogue.current &&
      !dialogue.current.open
    )
      dialogue.current.showModal()
  }, [publierOuvert, aArchiver])
  function chargerFiche(f: FicheEvenement) {
    setFiche({ ...f, parcours: parcoursInitial(f) })
    setPayant(f.prix_cents > 0)
  }
  function creer(confirme = false) {
    if (occupation) return
    if (modifie && !confirme) {
      setNavigation({ type: 'creer' })
      return
    }
    setDetail(null)
    chargerFiche(
      articleInitial ? ficheDepuisArticle(articleInitial) : nouvelleFiche(),
    )
    setModifie(false)
    setEtape(0)
    setVue('editeur')
    setErreur('')
    setNotice('')
  }
  async function ouvrir(cle: string, confirme = false) {
    if (occupation) return
    if (modifie && !confirme) {
      if (detail?.cle === cle) {
        setVue('editeur')
        return
      }
      setNavigation({ type: 'ouvrir', cle })
      return
    }
    setOccupation(true)
    setErreur('')
    try {
      const r = await appeler<Detail>(`/${encodeURIComponent(cle)}`)
      setDetail(r)
      chargerFiche(r.fiche)
      setModifie(false)
      setEtape(0)
      setVue('editeur')
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupation(false)
    }
  }
  function changer<K extends keyof FicheEvenement>(
    cle: K,
    valeur: FicheEvenement[K],
  ) {
    setFiche((f) => ({ ...f, [cle]: valeur }))
    setModifie(true)
    setNotice('')
  }
  function choisirArticle(id: string) {
    const item = articles.find((i) => i.id === id)
    if (item) chargerFiche(ficheDepuisArticle(item, fiche, !!fiche.nom))
    else {
      const suivante: FicheEvenement = {
        ...fiche,
        type: 'demande',
        inventaire_item_id: undefined,
        compte_gl: undefined,
        prix_cents: 0,
        depot_cents: 0,
        ressources: [],
        subdivisions: [],
        parcours: (fiche.parcours ?? [])
          .filter(
            (b) => !['creneau', 'offres', 'paiement', 'depot'].includes(b.bloc),
          )
          .map((b) =>
            b.bloc === 'confirmation' ? { ...b, joint_calendrier: false } : b,
          ),
      }
      if (fiche.graphe_parcours)
        suivante.graphe_parcours = ajusterGraphe(
          fiche.graphe_parcours,
          fiche.parcours ?? [],
          suivante.parcours ?? [],
        )
      chargerFiche(suivante)
    }
    setModifie(true)
    setNotice('')
  }
  async function actualiserArticles() {
    setOccupation(true)
    setErreur('')
    try {
      const r = await apiBibliotheque<DonneesInventaire>(baseUrl, 'inventaire')
      setArticles(r.items)
      setRelationsSalles(r.relations)
      const principal = r.items.find((i) => i.id === fiche.inventaire_item_id)
      const f = {
        ...fiche,
        ...(principal
          ? {
              prix_cents: principal.fiche.prix_cents,
              compte_gl: principal.fiche.compte_gl,
            }
          : {}),
        parcours: (fiche.parcours ?? []).map((b) =>
          b.bloc === 'offres'
            ? {
                ...b,
                options: b.options.map((o) => {
                  const i = r.items.find((i) => i.id === o.item_id)
                  return i
                    ? {
                        ...o,
                        nom: i.fiche.nom,
                        description: i.fiche.description,
                        montant_cents: i.fiche.prix_cents,
                        compte_gl: i.fiche.compte_gl,
                        unite: i.fiche.unite,
                        quantite_maximum: Math.min(
                          o.quantite_maximum,
                          i.fiche.quantite,
                          1000,
                        ),
                      }
                    : o
                }),
              }
            : b,
        ),
      }
      chargerFiche(f)
      setModifie(true)
      setNotice(
        'Éléments actualisés. Enregistrez et testez cette version avant de la publier.',
      )
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupation(false)
    }
  }
  function suivant() {
    if (!formulaire.current?.reportValidity()) return
    setEtape((e) => Math.min(3, e + 1))
    requestAnimationFrame(() => titre.current?.focus())
  }
  async function enregistrer(): Promise<Detail> {
    const r = await appeler<Detail>(
      detail ? `/${encodeURIComponent(detail.cle)}` : '',
      detail ? 'PUT' : 'POST',
      { fiche, ...(detail ? { version_id: detail.version_id } : {}) },
    )
    setDetail(r)
    chargerFiche(r.fiche)
    setModifie(false)
    setNotice(
      'Brouillon enregistré. Il est visible uniquement dans l’administration.',
    )
    await recharger()
    onChange()
    return r
  }
  async function agir(action: 'enregistrer' | 'apercu' | 'publier') {
    if (occupation) return
    setOccupation(true)
    setErreur('')
    try {
      const courant = !detail || modifie ? await enregistrer() : detail
      if (action === 'apercu') {
        const r = await appeler<Detail>(
          `/${encodeURIComponent(courant.cle)}/apercu`,
          'POST',
          { version_id: courant.version_id },
        )
        setDetail(r)
        setNotice(
          'Votre aperçu est prêt. Essayez le parcours avant de publier.',
        )
        onChange()
      }
      if (action === 'publier') {
        const r = await appeler<Detail>(
          `/${encodeURIComponent(courant.cle)}/publier`,
          'POST',
          {
            version_id: courant.version_id,
            citation: 'Publier cette version',
            confirmation_demonstration: accordDemo,
          },
        )
        setDetail(r)
        fermerDialogue()
        setNotice('C’est publié. Votre page est prête à être partagée.')
        await recharger()
        onChange()
      }
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupation(false)
    }
  }
  async function dupliquer(cle: string, confirme = false) {
    if (modifie && !confirme) {
      setNavigation({ type: 'dupliquer', cle })
      return
    }
    setOccupation(true)
    setErreur('')
    try {
      const r = await appeler<Detail>(
        `/${encodeURIComponent(cle)}/dupliquer`,
        'POST',
        {},
      )
      setDetail(r)
      chargerFiche(r.fiche)
      setModifie(false)
      setEtape(0)
      setVue('editeur')
      setNotice(
        'Une copie a été créée en brouillon. Personnalisez-la avant de la publier.',
      )
      await recharger()
      onChange()
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupation(false)
    }
  }
  async function archiver() {
    if (!aArchiver) return
    setOccupation(true)
    setErreur('')
    try {
      await appeler(`/${encodeURIComponent(aArchiver.cle)}/archiver`, 'POST', {
        version_id: aArchiver.version_id,
      })
      fermerDialogue()
      setNotice(
        'La page est archivée. Les réservations existantes restent disponibles.',
      )
      await recharger()
      onChange()
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupation(false)
    }
  }
  function fermerDialogue() {
    dialogue.current?.close()
    setPublierOuvert(false)
    setAArchiver(null)
    setAccord(false)
    setAccordDemo(false)
  }
  const items = liste.filter(
    (d) =>
      (filtre === 'archives'
        ? d.etat === 'archive'
        : filtre === 'brouillons'
          ? d.a_un_brouillon && d.etat !== 'archive'
          : d.etat !== 'archive') &&
      `${d.fiche.nom} ${d.fiche.lieu}`
        .toLowerCase()
        .includes(recherche.toLowerCase()),
  )
  const ecranDate = fiche.type === 'evenement'
  const demandeLibre = fiche.type === 'demande'
  const achat = fiche.type === 'achat'
  const article = articles.find((i) => i.id === fiche.inventaire_item_id)
  const workflow = (fiche.parcours ?? []).map(
    (b) => b.titre || NOMS_BLOCS[b.bloc],
  )

  return (
    <div class="ev-root">
      {erreur ? (
        <div class="ev-message ev-message-erreur" role="alert">
          <span>{erreur}</span>
          <button
            type="button"
            onClick={() => setErreur('')}
            aria-label="Fermer le message"
          >
            ×
          </button>
        </div>
      ) : null}
      {notice ? (
        <p class="ev-message" role="status">
          {notice}
        </p>
      ) : null}
      {vue === 'liste' ? (
        <>
          <div class="ev-entete">
            <div>
              <p>
                Composez les étapes de réservation, puis publiez votre parcours
                dans vos catalogues.
              </p>
            </div>
          </div>
          {modifie ? (
            <div class="ev-brouillon-local">
              <span>Votre création en cours vous attend.</span>
              <button type="button" onClick={() => setVue('editeur')}>
                Reprendre
              </button>
            </div>
          ) : null}
          <div class="ev-barre">
            <div class="ev-onglets" role="group" aria-label="État des services">
              {[
                ['actifs', 'Tous'],
                ['brouillons', 'Brouillons'],
                ['archives', 'Archives'],
              ].map(([cle, libelle]) => (
                <button
                  type="button"
                  aria-pressed={filtre === cle}
                  onClick={() => setFiltre(cle!)}
                >
                  {libelle}
                </button>
              ))}
            </div>
            <label class="ev-recherche">
              <span class="ev-sr">Rechercher un service</span>
              <input
                type="search"
                placeholder="Rechercher…"
                value={recherche}
                onInput={(e) => setRecherche(e.currentTarget.value)}
              />
            </label>
          </div>
          {charge ? (
            <div class="ev-vide" role="status">
              <div class="ev-pastille">
                <IconeEvenement />
              </div>
              <h3>Chargement de vos parcours…</h3>
            </div>
          ) : items.length ? (
            <div class="ev-cartes">
              {items.map((d) => (
                <article class="ev-carte" key={d.cle}>
                  <div class="ev-carte-haut">
                    <span
                      aria-hidden="true"
                      class={`ev-pastille ${d.fiche.type === 'location' ? 'ev-pastille-violet' : ''}`}
                    >
                      <IconeEvenement salle={d.fiche.type === 'location'} />
                    </span>
                    <span class={`ev-statut ev-statut-${d.etat}`}>
                      {d.etat === 'publie'
                        ? 'En ligne'
                        : d.etat === 'archive'
                          ? 'Archivé'
                          : 'Brouillon'}
                    </span>
                  </div>
                  <p class="ev-surtitre">
                    {d.fiche.type === 'achat'
                      ? 'Achat · sans créneau'
                      : d.fiche.type === 'demande'
                        ? 'Demande personnalisée'
                        : d.fiche.type === 'evenement'
                          ? 'Événement · inscriptions'
                          : 'Réservation d’espace'}
                  </p>
                  <h3>
                    <button type="button" onClick={() => void ouvrir(d.cle)}>
                      {d.fiche.nom}
                    </button>
                  </h3>
                  <p class="ev-lieu">{d.fiche.lieu}</p>
                  <div class="ev-carte-infos">
                    <span>
                      {d.fiche.type === 'achat'
                        ? 'Achat · sans créneau'
                        : d.fiche.type === 'demande'
                          ? 'Formulaires et suivi'
                          : d.fiche.type === 'evenement'
                            ? dateLisible(d.fiche.date)
                            : `${d.fiche.ressources.length} espace${d.fiche.ressources.length > 1 ? 's' : ''} disponible${d.fiche.ressources.length > 1 ? 's' : ''}`}
                    </span>
                    <strong>
                      {d.fiche.type === 'achat'
                        ? 'Achat · sans créneau'
                        : d.fiche.type === 'demande'
                          ? `${d.fiche.parcours?.length ?? 0} étapes`
                          : d.fiche.type === 'evenement'
                            ? `${d.places_prises} / ${d.fiche.capacite} places`
                            : `${d.fiche.duree_minimum / 60}–${d.fiche.duree_maximum / 60} h par réservation`}
                    </strong>
                  </div>
                  {d.fiche.type === 'evenement' ? (
                    <div
                      class="ev-jauge"
                      aria-label={`${d.places_prises} places prises sur ${d.fiche.capacite}`}
                    >
                      <span
                        style={{
                          width: `${Math.min(100, (d.places_prises * 100) / d.fiche.capacite)}%`,
                        }}
                      />
                    </div>
                  ) : null}
                  <div class="ev-carte-pied">
                    <button
                      type="button"
                      class="ev-secondaire"
                      onClick={() => void ouvrir(d.cle)}
                    >
                      {modifiable ? 'Gérer' : 'Consulter'}
                    </button>
                    {d.etat === 'publie' ? (
                      <a href={d.url} target="_blank" rel="noreferrer">
                        Voir la page ↗
                      </a>
                    ) : (
                      <span>
                        {d.fiche.prix_cents
                          ? prix(d.fiche.prix_cents)
                          : 'Gratuit'}
                      </span>
                    )}
                    {modifiable ? (
                      <details class="ev-menu">
                        <summary aria-label={`Actions pour ${d.fiche.nom}`}>
                          •••
                        </summary>
                        <div>
                          <button
                            type="button"
                            disabled={occupation}
                            onClick={() => void dupliquer(d.cle)}
                          >
                            Dupliquer en brouillon
                          </button>
                          {d.etat !== 'archive' ? (
                            <button
                              type="button"
                              onClick={() => setAArchiver(d)}
                            >
                              Archiver
                            </button>
                          ) : null}
                        </div>
                      </details>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div class="ev-vide">
              <div class="ev-pastille">
                <IconeEvenement />
              </div>
              <h3>
                {recherche
                  ? 'Aucun résultat'
                  : filtre === 'archives'
                    ? 'Rien dans les archives'
                    : 'Tout commence par une idée.'}
              </h3>
              <p>
                {recherche
                  ? 'Essayez un autre nom ou un autre lieu.'
                  : 'Choisissez un élément de l’inventaire, puis assemblez vos formulaires, options, contrats et paiements.'}
              </p>
              {modifiable && filtre !== 'archives' && !recherche ? (
                <button
                  class="ev-primaire"
                  type="button"
                  onClick={() => creer()}
                >
                  Créer votre premier parcours
                </button>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <>
          <div class="ev-retour">
            <button type="button" onClick={() => setVue('liste')}>
              ← Tous les parcours
            </button>
            <span>
              {detail
                ? `Version ${detail.version}${modifie ? ' · Modifications en cours' : ''}`
                : 'Nouvelle création'}
            </span>
          </div>
          <div class="ev-editeur ev-configuration">
            <aside class="ev-etapes">
              <div class="ev-pastille">
                <IconeEvenement salle={!ecranDate} />
              </div>
              <h2>{detail ? 'Votre parcours' : 'Créer simplement.'}</h2>
              <p>
                Quelques choix.
                <br />
                Un parcours prêt à partager.
              </p>
              <nav aria-label="Configuration du parcours">
                {ETAPES.map((nom, index) => (
                  <button
                    type="button"
                    class={etape === index ? 'ev-etape-active' : ''}
                    aria-current={etape === index ? 'page' : undefined}
                    onClick={() => {
                      setEtape(index)
                    }}
                  >
                    {(demandeLibre || achat) && index === 1
                      ? 'Conditions'
                      : nom}
                  </button>
                ))}
              </nav>
              <div class="ev-rappel">
                <strong>
                  {modifiable ? 'Vous gardez le contrôle.' : 'Accès en lecture'}
                </strong>
                <p>
                  {modifiable
                    ? 'Rien n’est publié avant votre confirmation.'
                    : 'Votre rôle permet de consulter ce service.'}
                </p>
              </div>
            </aside>
            <form
              class="ev-formulaire"
              ref={formulaire}
              onSubmit={(e) => {
                e.preventDefault()
                if (etape < 3) suivant()
                else void agir('enregistrer')
              }}
            >
              <div class="ev-formulaire-entete">
                <p class="ev-surtitre">
                  {ETAPES[etape]} · {fiche.nom || 'Nouvelle création'}
                </p>
                <h2 ref={titre} tabIndex={-1}>
                  {
                    [
                      'Un élément. Son parcours.',
                      achat
                        ? 'Une commande sans détour.'
                        : demandeLibre
                          ? 'Un service à votre mesure.'
                          : ecranDate
                            ? 'Un rendez-vous, des places.'
                            : 'Des créneaux qui vous conviennent.',
                      'Composez votre parcours.',
                      'Tout est prêt à être essayé.',
                    ][etape]
                  }
                </h2>
                <p>
                  {
                    [
                      'Les renseignements que vos participants verront en premier.',
                      'Les disponibilités sont vérifiées automatiquement à chaque réservation.',
                      'Ajoutez, déplacez et personnalisez les étapes. Vous décidez de l’expérience.',
                      'Relisez les détails, puis essayez le parcours comme un participant.',
                    ][etape]
                  }
                </p>
              </div>
              {fiche.inventaire_item_id ||
              fiche.parcours?.some(
                (b) => b.bloc === 'offres' && b.options.length,
              ) ? (
                <div class="ev-inventaire-sync">
                  <p>
                    Les prix et comptes GL sont enregistrés avec chaque version
                    du parcours.
                  </p>
                  <button
                    type="button"
                    class="ev-lien"
                    disabled={!modifiable || occupation}
                    onClick={() => void actualiserArticles()}
                  >
                    Actualiser les éléments
                  </button>
                </div>
              ) : null}
              <fieldset disabled={!modifiable || occupation}>
                {etape === 0 ? (
                  <div class="ev-section-champs">
                    <section class="ev-article-source">
                      <Champ
                        titre="Élément principal"
                        aide="Les tarifs, comptes GL et capacités sont définis dans l’inventaire."
                      >
                        <select
                          value={
                            fiche.inventaire_item_id ??
                            (fiche.type === 'demande' ? '' : 'legacy')
                          }
                          onChange={(e) =>
                            choisirArticle(e.currentTarget.value)
                          }
                        >
                          <option
                            value=""
                            disabled={!!detail && fiche.type !== 'demande'}
                          >
                            Demande libre · sans élément
                          </option>
                          {!fiche.inventaire_item_id &&
                          fiche.type !== 'demande' ? (
                            <option value="legacy" disabled>
                              Configuration existante · choisir un élément pour
                              la relier
                            </option>
                          ) : null}
                          {articles
                            .filter(
                              (i) =>
                                (i.fiche.actif ||
                                  i.id === fiche.inventaire_item_id) &&
                                (!detail ||
                                  (i.fiche.nature === 'achat'
                                    ? 'achat'
                                    : i.fiche.type === 'activite'
                                      ? 'evenement'
                                      : 'location') === fiche.type),
                            )
                            .map((i) => (
                              <option value={i.id} key={i.id}>
                                {i.fiche.nom}
                                {!i.fiche.actif ? ' · Désactivé' : ''}
                              </option>
                            ))}
                        </select>
                      </Champ>
                      {article ? (
                        <div class="ev-article-resume">
                          <div>
                            <strong>
                              {prixInventaire(fiche.prix_cents)}{' '}
                              <small>
                                {UNITES_INVENTAIRE[article.fiche.unite]}
                              </small>
                            </strong>
                            <span>
                              {fiche.compte_gl
                                ? `Compte GL · ${fiche.compte_gl.compte_credit}`
                                : 'Compte GL à configurer dans l’inventaire'}
                            </span>
                          </div>
                          {onInventaire ? (
                            <button
                              type="button"
                              class="ev-lien"
                              onClick={() => onInventaire(article.id)}
                            >
                              Modifier l’élément ↗
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      <p class="ev-note">
                        Un même élément peut avoir plusieurs parcours :
                        réservation directe, demande avec approbation, événement
                        ou contrat personnalisé.
                      </p>
                    </section>
                    <Champ titre="Nom du parcours public">
                      <input
                        value={fiche.nom}
                        placeholder={
                          ecranDate
                            ? 'Ex. Atelier de céramique'
                            : demandeLibre
                              ? 'Ex. Demande de soutien à un projet'
                              : 'Ex. Location de la salle communautaire'
                        }
                        required
                        minLength={2}
                        maxLength={120}
                        onInput={(e) => changer('nom', e.currentTarget.value)}
                      />
                    </Champ>
                    <Champ
                      titre="Présentation"
                      aide="Décrivez ce qui attend le public, simplement."
                    >
                      <textarea
                        value={fiche.description}
                        rows={4}
                        required
                        minLength={10}
                        maxLength={4000}
                        placeholder="Une courte description pour donner envie de participer…"
                        onInput={(e) =>
                          changer('description', e.currentTarget.value)
                        }
                      />
                    </Champ>
                    <div class="ev-deux">
                      <Champ titre="Lieu">
                        <input
                          value={fiche.lieu}
                          placeholder="Ex. Centre communautaire"
                          required={!demandeLibre && !achat}
                          minLength={demandeLibre || achat ? 0 : 2}
                          onInput={(e) =>
                            changer('lieu', e.currentTarget.value)
                          }
                        />
                      </Champ>
                      <Champ titre="Adresse" aide="Facultatif">
                        <input
                          value={fiche.adresse}
                          placeholder="Rue, ville"
                          onInput={(e) =>
                            changer('adresse', e.currentTarget.value)
                          }
                        />
                      </Champ>
                    </div>
                  </div>
                ) : null}
                {etape === 1 ? (
                  <div class="ev-section-champs">
                    {achat ? (
                      <div class="ev-demande-libre">
                        <h3>Choisir, compléter, commander.</h3>
                        <p>
                          Ce parcours ne demande pas de date. La quantité de
                          l’article principal et les produits complémentaires se
                          configurent dans l’étape Équipements et options.
                        </p>
                        <button
                          class="ev-secondaire"
                          type="button"
                          onClick={() => setEtape(2)}
                        >
                          Composer le parcours →
                        </button>
                      </div>
                    ) : demandeLibre ? (
                      <div class="ev-demande-libre">
                        <h3>Le bon formulaire, au bon moment.</h3>
                        <p>
                          Cette demande ne réserve pas de salle ou de place.
                          Composez les formulaires utiles, ajoutez une
                          validation par votre équipe et faites signer un
                          contrat si nécessaire.
                        </p>
                        <button
                          class="ev-secondaire"
                          type="button"
                          onClick={() => setEtape(2)}
                        >
                          Composer le parcours →
                        </button>
                      </div>
                    ) : ecranDate ? (
                      <>
                        <Champ titre="Date de l’événement">
                          <input
                            type="date"
                            value={fiche.date}
                            required
                            onInput={(e) =>
                              changer('date', e.currentTarget.value)
                            }
                          />
                        </Champ>
                        <div class="ev-deux">
                          <Champ titre="Début">
                            <input
                              type="time"
                              step={60}
                              value={fiche.debut}
                              required
                              onInput={(e) =>
                                changer('debut', e.currentTarget.value)
                              }
                            />
                          </Champ>
                          <Champ titre="Fin">
                            <input
                              type="time"
                              step={60}
                              value={fiche.fin}
                              required
                              onInput={(e) =>
                                changer('fin', e.currentTarget.value)
                              }
                            />
                          </Champ>
                        </div>
                        <p class="ev-note">
                          Heures de {fuseau}. Une séance dans la même journée.
                        </p>
                        <div class="ev-deux">
                          <Champ
                            titre="Nombre total de places"
                            aide="Les inscriptions ferment quand toutes les places sont prises."
                          >
                            <input
                              type="number"
                              value={fiche.capacite}
                              readOnly={!!fiche.inventaire_item_id}
                              min={1}
                              max={100000}
                              required
                              onInput={(e) =>
                                changer(
                                  'capacite',
                                  Number(e.currentTarget.value),
                                )
                              }
                            />
                          </Champ>
                          <Champ
                            titre="Personnes par inscription"
                            aide="1 pour une inscription individuelle ; plus pour accueillir les groupes."
                          >
                            <input
                              type="number"
                              value={fiche.maximum_par_inscription}
                              min={1}
                              max={Math.min(fiche.capacite, 1000)}
                              required
                              onInput={(e) =>
                                changer(
                                  'maximum_par_inscription',
                                  Number(e.currentTarget.value),
                                )
                              }
                            />
                          </Champ>
                        </div>
                      </>
                    ) : (
                      <>
                        {!fiche.inventaire_item_id ? (
                          <>
                            <SallesEditor fiche={fiche} sallesLiees={articles.filter((i) => relationsSalles.some((r) => r.item_a === i.id || r.item_b === i.id)).flatMap((i) => i.fiche.ressource_cle ? [i.fiche.ressource_cle] : [])} changer={(espaces) => {
                              setFiche((f) => ({ ...f, ...espaces, parcours: f.parcours?.map((b) => b.bloc === 'creneau' ? { ...b, choix_de_la_ressource: espaces.ressources.length > 1 } : b) }))
                              setModifie(true)
                              setNotice('')
                            }} />
                            <LiensSallesInventaire ressources={fiche.ressources} articles={articles} relations={relationsSalles} onInventaire={onInventaire} />
                          </>
                        ) : (
                          <p class="ev-note">
                            Disponibilités de «{' '}
                            {article?.fiche.nom ?? fiche.nom} ». Vous pouvez
                            restreindre ici les heures proposées par ce
                            parcours. Les réservations et les incompatibilités
                            de l’article restent partagées.
                          </p>
                        )}
                        <div>
                          <h3>Jours d’ouverture</h3>
                          <div class="ev-jours">
                            {JOURS.map((j) => (
                              <button
                                type="button"
                                aria-pressed={fiche.jours.includes(j.cle)}
                                onClick={() =>
                                  changer(
                                    'jours',
                                    fiche.jours.includes(j.cle)
                                      ? fiche.jours.filter((d) => d !== j.cle)
                                      : [...fiche.jours, j.cle],
                                  )
                                }
                              >
                                {j.nom}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div class="ev-deux">
                          <Champ titre="Ouverture">
                            <input
                              type="time"
                              step={60}
                              value={fiche.ouverture}
                              required
                              onInput={(e) =>
                                changer('ouverture', e.currentTarget.value)
                              }
                            />
                          </Champ>
                          <Champ
                            titre="Fermeture"
                            aide={
                              fiche.fermeture === '24:00'
                                ? 'Minuit (fin de journée)'
                                : undefined
                            }
                          >
                            <input
                              type="time"
                              step={60}
                              value={
                                fiche.fermeture === '24:00'
                                  ? '23:59'
                                  : fiche.fermeture
                              }
                              required
                              onInput={(e) =>
                                changer('fermeture', e.currentTarget.value)
                              }
                            />
                          </Champ>
                        </div>
                        <div class="ev-deux">
                          <Champ titre="Durée minimale">
                            <Duree
                              valeur={fiche.duree_minimum}
                              minimum={fiche.pas_minutes}
                              pas={fiche.pas_minutes}
                              changer={(v) => changer('duree_minimum', v)}
                            />
                          </Champ>
                          <Champ titre="Durée maximale">
                            <Duree
                              valeur={fiche.duree_maximum}
                              minimum={fiche.duree_minimum}
                              pas={fiche.pas_minutes}
                              changer={(v) => changer('duree_maximum', v)}
                            />
                          </Champ>
                        </div>
                        <details class="ev-avance">
                          <summary>Options de disponibilité</summary>
                          <div class="ev-deux">
                            <Champ titre="Créneaux par pas de">
                              <select
                                value={fiche.pas_minutes}
                                onChange={(e) =>
                                  changer(
                                    'pas_minutes',
                                    Number(e.currentTarget.value),
                                  )
                                }
                              >
                                {[15, 30, 60, 120].map((n) => (
                                  <option value={n}>{n} minutes</option>
                                ))}
                              </select>
                            </Champ>
                            <Champ titre="Ouverture des réservations">
                              <select
                                value={fiche.horizon_jours}
                                onChange={(e) =>
                                  changer(
                                    'horizon_jours',
                                    Number(e.currentTarget.value),
                                  )
                                }
                              >
                                {[
                                  7,
                                  14,
                                  30,
                                  60,
                                  90,
                                  180,
                                  365,
                                  ...(![7, 14, 30, 60, 90, 180, 365].includes(
                                    fiche.horizon_jours,
                                  )
                                    ? [fiche.horizon_jours]
                                    : []),
                                ].map((n) => (
                                  <option value={n}>
                                    Jusqu’à {n} jours d’avance
                                  </option>
                                ))}
                              </select>
                            </Champ>
                          </div>
                        </details>
                      </>
                    )}
                    {!demandeLibre && !fiche.inventaire_item_id ? (
                      <section class="ev-prix">
                        <div>
                          <h3>
                            {ecranDate
                              ? 'Prix de l’inscription'
                              : 'Tarif de location'}
                          </h3>
                          <p class="ev-note">
                            Les prix sont en dollars canadiens. Les taxes de
                            l’organisation s’appliquent si elles sont
                            configurées.
                          </p>
                        </div>
                        <div class="ev-choix-prix">
                          <button
                            type="button"
                            aria-pressed={!payant}
                            onClick={() => {
                              setPayant(false)
                              changer('prix_cents', 0)
                            }}
                          >
                            Gratuit
                          </button>
                          <button
                            type="button"
                            aria-pressed={payant}
                            onClick={() => {
                              setPayant(true)
                              changer('prix_cents', fiche.prix_cents || 1000)
                            }}
                          >
                            Payant
                          </button>
                        </div>
                        {payant ? (
                          <Champ
                            titre={
                              ecranDate
                                ? 'Prix par personne ($ CA)'
                                : 'Prix par heure ($ CA)'
                            }
                          >
                            <Montant
                              valeur={fiche.prix_cents}
                              changer={(v) => changer('prix_cents', v)}
                            />
                          </Champ>
                        ) : null}
                      </section>
                    ) : null}
                  </div>
                ) : null}
                {etape === 2 ? (
                  <ParcoursEditor
                    fiche={fiche}
                    changer={(f) => {
                      setFiche(f)
                      setPayant(f.prix_cents > 0)
                      setModifie(true)
                      setNotice('')
                    }}
                    baseUrl={baseUrl}
                    lecture={!modifiable || occupation}
                    onAuth={onAuth}
                    visible={visible}
                    cleService={detail?.cle}
                    versionId={detail?.version_id}
                    onInventaire={onInventaire}
                  />
                ) : null}
                {etape === 3 ? (
                  <div class="ev-section-champs">
                    <article class="ev-recap">
                      <div class="ev-recap-haut">
                        <span class="ev-pastille">
                          <IconeEvenement salle={!ecranDate} />
                        </span>
                        <span class="ev-statut">
                          {detail?.etat === 'publie' &&
                          !modifie &&
                          !detail.a_un_brouillon
                            ? 'En ligne'
                            : 'Prêt à vérifier'}
                        </span>
                      </div>
                      <p class="ev-surtitre">
                        {demandeLibre
                          ? 'Demande personnalisée'
                          : ecranDate
                            ? 'Événement · inscriptions'
                            : 'Réservation d’espace'}
                      </p>
                      <h3>{fiche.nom || 'Votre parcours'}</h3>
                      <p>{fiche.description}</p>
                      <dl>
                        <div>
                          <dt>Lieu</dt>
                          <dd>
                            {fiche.lieu}
                            {fiche.adresse ? ` · ${fiche.adresse}` : ''}
                          </dd>
                        </div>
                        <div>
                          <dt>
                            {achat
                              ? 'Commande sans créneau'
                              : demandeLibre
                                ? 'Parcours'
                                : ecranDate
                                  ? 'Quand'
                                  : 'Disponibilité'}
                          </dt>
                          <dd>
                            {achat
                              ? 'Commande sans créneau'
                              : demandeLibre
                                ? 'Selon les étapes que vous avez choisies'
                                : ecranDate
                                  ? `${dateLisible(fiche.date)} · ${fiche.debut}–${fiche.fin}`
                                  : `${fiche.jours.map((j) => JOURS.find((d) => d.cle === j)?.nom).join(', ')} · ${fiche.ouverture}–${fiche.fermeture}`}
                          </dd>
                        </div>
                        <div>
                          <dt>
                            {achat
                              ? 'Commande sans créneau'
                              : demandeLibre
                                ? 'Étapes'
                                : ecranDate
                                  ? 'Places'
                                  : 'Salles'}
                          </dt>
                          <dd>
                            {achat
                              ? 'Commande sans créneau'
                              : demandeLibre
                                ? `${workflow.length} étapes configurées`
                                : ecranDate
                                  ? `${fiche.capacite} au total · ${fiche.maximum_par_inscription} par inscription`
                                  : fiche.ressources
                                      .map((r) => r.nom)
                                      .join(', ')}
                          </dd>
                        </div>
                        <div>
                          <dt>Prix</dt>
                          <dd>
                            {fiche.prix_cents
                              ? `${prix(fiche.prix_cents)} ${article ? UNITES_INVENTAIRE[article.fiche.unite] : ecranDate ? 'par personne' : achat ? 'par unité' : 'par heure'}`
                              : 'Gratuit'}
                            {fiche.depot_cents
                              ? ` · Dépôt de ${prix(fiche.depot_cents)}`
                              : ''}
                          </dd>
                        </div>
                      </dl>
                    </article>
                    <div>
                      <h3>Le parcours de vos participants</h3>
                      <ol class="ev-parcours-recap">
                        {workflow.map((w) => (
                          <li>{w}</li>
                        ))}
                      </ol>
                    </div>
                    {detail?.avance ? (
                      <p class="ev-note">
                        Ce service comprend des règles avancées. Les réglages
                        que vous n’avez pas modifiés sont conservés.
                      </p>
                    ) : null}
                    {detail?.manques?.filter((m) => m.gravite === 'bloquant')
                      .length ? (
                      <div class="ev-message ev-message-erreur">
                        <strong>À compléter avant publication</strong>
                        <ul>
                          {detail.manques
                            .filter((m) => m.gravite === 'bloquant')
                            .map((m) => (
                              <li>{m.message}</li>
                            ))}
                        </ul>
                      </div>
                    ) : null}
                    {modifiable ? (
                      <div class="ev-test">
                        <div>
                          <h3>Essayez avant d’ouvrir.</h3>
                          <p>
                            L’aperçu permet de suivre tout le parcours. Les
                            réservations et les paiements y sont simulés.
                          </p>
                        </div>
                        <button
                          class="ev-secondaire"
                          type="button"
                          onClick={() => void agir('apercu')}
                          disabled={occupation}
                        >
                          Préparer l’aperçu
                        </button>
                        {detail?.apercu_a_jour && !modifie ? (
                          <a
                            class="ev-primaire"
                            href={
                              detail.url_apercu ?? `${detail.url}?univers=essai`
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            Essayer le parcours ↗
                          </a>
                        ) : null}
                      </div>
                    ) : (
                      <p class="ev-note">
                        Un gestionnaire peut préparer et essayer un aperçu avant
                        publication.
                      </p>
                    )}
                  </div>
                ) : null}
              </fieldset>
              <div class="ev-formulaire-pied">
                <button
                  class="ev-secondaire"
                  type="button"
                  onClick={() =>
                    etape > 0 ? setEtape(etape - 1) : setVue('liste')
                  }
                  disabled={occupation}
                >
                  {etape ? 'Précédent' : 'Fermer'}
                </button>
                <span>
                  {occupation
                    ? 'Enregistrement…'
                    : detail && !modifie
                      ? 'Modifications enregistrées'
                      : 'Enregistré comme brouillon avant publication'}
                </span>
                {etape < 3 ? (
                  <div class="ev-actions">
                    {modifiable ? (
                      <button
                        class="ev-secondaire"
                        type="button"
                        disabled={occupation || Boolean(detail && !modifie)}
                        onClick={() => void agir('enregistrer')}
                      >
                        Enregistrer le brouillon
                      </button>
                    ) : null}
                    <button
                      class="ev-primaire"
                      type="button"
                      onClick={() => setEtape(etape + 1)}
                    >
                      Continuer →
                    </button>
                  </div>
                ) : modifiable ? (
                  <div class="ev-actions">
                    <button
                      class="ev-secondaire"
                      type="button"
                      onClick={() => void agir('enregistrer')}
                      disabled={occupation || Boolean(detail && !modifie)}
                    >
                      Enregistrer le brouillon
                    </button>
                    <button
                      class="ev-primaire"
                      type="button"
                      disabled={
                        occupation ||
                        !detail?.apercu_a_jour ||
                        modifie ||
                        (!detail.a_un_brouillon && detail.etat === 'publie')
                      }
                      onClick={() => {
                        setAccord(false)
                        setAccordDemo(false)
                        setPublierOuvert(true)
                      }}
                    >
                      Publier
                    </button>
                  </div>
                ) : null}
              </div>
            </form>
          </div>
        </>
      )}
      {navigation ? (
        <dialog
          ref={dialogueNavigation}
          class="ev-dialogue"
          aria-labelledby="ev-navigation-titre"
          onCancel={() => setNavigation(null)}
        >
          <h2 id="ev-navigation-titre">Garder votre travail ?</h2>
          <p>
            Vos modifications ne sont pas encore enregistrées. En ouvrant{' '}
            {navigation.type === 'creer'
              ? 'une nouvelle création'
              : 'un autre service'}
            , vous les perdrez.
          </p>
          <div class="ev-actions">
            <button
              class="ev-primaire"
              type="button"
              onClick={() => {
                dialogueNavigation.current?.close()
                setNavigation(null)
                setVue('editeur')
              }}
            >
              Revenir à mon brouillon
            </button>
            <button
              class="ev-secondaire"
              type="button"
              onClick={() => {
                const suivant = navigation
                dialogueNavigation.current?.close()
                setNavigation(null)
                if (suivant.type === 'creer') creer(true)
                else if (suivant.type === 'ouvrir')
                  void ouvrir(suivant.cle!, true)
                else void dupliquer(suivant.cle!, true)
              }}
            >
              Abandonner les modifications
            </button>
          </div>
        </dialog>
      ) : null}
      {publierOuvert || aArchiver ? (
        <dialog
          ref={dialogue}
          class="ev-dialogue"
          aria-labelledby="ev-dialogue-titre"
          onCancel={(e) => {
            if (occupation) e.preventDefault()
            else fermerDialogue()
          }}
        >
          <h2 id="ev-dialogue-titre">
            {aArchiver ? 'Archiver ce parcours ?' : 'Publier ce parcours ?'}
          </h2>
          <p>
            {aArchiver
              ? 'La page ne recevra plus de nouvelles réservations. Les demandes et les contrats existants restent accessibles.'
              : `« ${fiche.nom} » sera accessible au public. Vous pourrez partager son lien immédiatement.`}
          </p>
          {!aArchiver ? (
            <>
              <label class="ev-accord">
                <input
                  type="checkbox"
                  checked={accord}
                  onChange={(e) => setAccord(e.currentTarget.checked)}
                />
                J’ai essayé le parcours et je confirme la publication.
              </label>
              {detail?.paiement_en_demonstration ? (
                <label class="ev-accord ev-accord-demo">
                  <input
                    type="checkbox"
                    checked={accordDemo}
                    onChange={(e) => setAccordDemo(e.currentTarget.checked)}
                  />
                  <span>
                    Les paiements sont simulés. Je confirme publier cette
                    démonstration ; aucun montant ne sera encaissé.
                  </span>
                </label>
              ) : null}
            </>
          ) : null}
          {erreur ? (
            <p class="ev-message ev-message-erreur" role="alert">
              {erreur}
            </p>
          ) : null}
          <div class="ev-actions">
            <button
              class="ev-secondaire"
              type="button"
              disabled={occupation}
              onClick={fermerDialogue}
            >
              Revenir
            </button>
            <button
              class="ev-primaire"
              type="button"
              disabled={
                occupation ||
                (!aArchiver &&
                  (!accord ||
                    (detail?.paiement_en_demonstration && !accordDemo)))
              }
              onClick={() =>
                aArchiver ? void archiver() : void agir('publier')
              }
            >
              {occupation
                ? 'Un instant…'
                : aArchiver
                  ? 'Archiver'
                  : 'Confirmer la publication'}
            </button>
          </div>
        </dialog>
      ) : null}
    </div>
  )
}

export const stylesEvenements = `
.ev-root{--ev-bleu:#391d44;--ev-encre:#2a1632;--ev-gris:#6b5a70;--ev-ligne:#eee6f2;color:var(--ev-encre);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.ev-root *{box-sizing:border-box}.ev-root button,.ev-root input,.ev-root select,.ev-root textarea{font:inherit}.ev-root button,.ev-root a{touch-action:manipulation}.ev-root button{cursor:pointer}.ev-root button:disabled{cursor:default;opacity:.42}.ev-root input,.ev-root select,.ev-root textarea{width:100%;border:1px solid #d9cfdc;border-radius:10px;background:#fff;color:var(--ev-encre);padding:12px 14px;min-height:46px;outline:none}.ev-root textarea{resize:vertical;line-height:1.55}.ev-root :is(button,a,input,select,textarea,summary):focus-visible{outline:3px solid #c3c3ed;outline-offset:3px}.ev-root input:focus,.ev-root textarea:focus,.ev-root select:focus{border-color:var(--ev-bleu);box-shadow:0 0 0 3px #391d4412}.ev-root input[type=checkbox]{width:18px;min-height:18px;height:18px;accent-color:var(--ev-bleu);flex-shrink:0}.ev-root fieldset{border:0;padding:0;margin:0;min-width:0}.ev-root h2,.ev-root h3,.ev-root p{margin:0}.ev-root h2{font-size:28px;line-height:1.18;letter-spacing:-1px;font-weight:650}.ev-root h3{font-size:18px;line-height:1.35;letter-spacing:-.35px;font-weight:600}.ev-root p{line-height:1.55}.ev-root a{color:var(--ev-bleu);text-decoration:none}.ev-root a:hover{text-decoration:underline}.ev-entete{display:flex;justify-content:space-between;align-items:center;gap:24px;margin:8px 0 30px}.ev-entete p:not(.ev-surtitre){color:var(--ev-gris);font-size:15px;margin-top:10px}.ev-surtitre{font-size:11px;letter-spacing:1.2px;text-transform:uppercase;color:var(--ev-gris);font-weight:650;margin-bottom:9px!important}.ev-primaire,.ev-secondaire{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:44px;padding:11px 18px;border-radius:10px;border:1px solid transparent;white-space:nowrap;font-weight:550;font-size:14px!important;text-decoration:none!important}.ev-primaire{background:var(--ev-bleu);color:#fff!important;box-shadow:0 2px 5px #391d4413}.ev-primaire:hover{background:#2a1632}.ev-secondaire{background:#f7f3f9;color:var(--ev-encre);border-color:#ece2f0}.ev-secondaire:hover{background:#f1eaf4}.ev-barre{display:flex;align-items:center;justify-content:space-between;gap:18px;margin-bottom:24px}.ev-onglets{display:flex;padding:4px;background:#f1e9f4;border-radius:10px;gap:2px}.ev-onglets button{border:0;background:none;color:var(--ev-gris);padding:9px 13px;border-radius:7px;font-size:13px;font-weight:550;white-space:nowrap;min-height:36px}.ev-onglets button[aria-pressed=true]{background:#fff;color:var(--ev-encre);box-shadow:0 1px 3px #00000012}.ev-recherche{max-width:250px}.ev-recherche input{background:#fff;border-color:var(--ev-ligne);padding-left:15px;min-height:42px}.ev-cartes{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:20px}.ev-carte{background:#fff;border:1px solid var(--ev-ligne);border-radius:18px;padding:24px;box-shadow:0 3px 15px #00000003;display:flex;flex-direction:column;min-height:308px;transition:box-shadow .15s,transform .15s}.ev-carte:hover{box-shadow:0 8px 28px #00000009;transform:translateY(-2px)}.ev-carte-haut{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}.ev-pastille{display:inline-flex;align-items:center;justify-content:center;width:46px;height:46px;flex-shrink:0;background:#f6f0f9;color:#391d44;border-radius:13px;font-size:25px;font-weight:400}.ev-pastille-violet{color:#391d44;background:#f2ebf7}.ev-statut{font-size:11px;letter-spacing:.2px;font-weight:600;background:#f5f0f7;border-radius:20px;padding:6px 10px;color:#6b5a70}.ev-statut-publie{color:#217246;background:#edf8f1}.ev-statut-archive{color:#856f8d;background:#f7f3f9}.ev-carte h3 button{background:none;border:0;padding:0;color:inherit;text-align:left;font-weight:inherit;font-size:inherit;line-height:inherit}.ev-lieu{font-size:13px;color:var(--ev-gris);margin-top:5px!important}.ev-carte-infos{display:flex;flex-direction:column;gap:6px;margin-top:23px;font-size:13px}.ev-carte-infos span{color:var(--ev-gris)}.ev-carte-infos strong{font-weight:550}.ev-jauge{height:4px;background:#f5f0f7;border-radius:4px;overflow:hidden;margin-top:12px}.ev-jauge span{height:100%;display:block;background:#aeafe7;border-radius:4px}.ev-carte-pied{display:flex;align-items:center;gap:12px;border-top:1px solid #f5f0f7;margin-top:22px;padding-top:18px;font-size:12px}.ev-carte-pied>.ev-secondaire{padding:9px 15px;min-height:39px;font-size:12px!important}.ev-carte-pied>a,.ev-carte-pied>span{margin-left:auto;white-space:nowrap}.ev-menu{position:relative}.ev-menu summary{list-style:none;cursor:pointer;padding:11px 4px;letter-spacing:1px}.ev-menu>div{position:absolute;right:0;top:36px;z-index:4;width:205px;background:#fff;border:1px solid var(--ev-ligne);border-radius:12px;box-shadow:0 8px 25px #0002;padding:5px}.ev-menu button{display:block;width:100%;text-align:left;padding:12px;background:none;border:0;border-radius:7px;font-size:13px}.ev-menu button:hover{background:#f7f3f9}.ev-vide{background:#fff;border:1px solid var(--ev-ligne);border-radius:18px;min-height:330px;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;padding:40px;gap:17px}.ev-vide p{max-width:440px;font-size:14px;color:var(--ev-gris)}.ev-message{display:flex;gap:12px;align-items:center;justify-content:space-between;background:#f8f4fb;border:1px solid #efe5f5;color:#391d44;border-radius:12px;padding:14px 18px;font-size:14px;margin-bottom:20px!important}.ev-message-erreur{background:#fff4f2;border-color:#f1d7d1;color:#913f31;display:block}.ev-message-erreur>button{float:right;border:0;background:none;padding:0 4px}.ev-message ul{padding-left:20px;margin-bottom:0}.ev-brouillon-local{display:flex;justify-content:space-between;align-items:center;background:#fff;border:1px solid var(--ev-ligne);padding:15px 20px;border-radius:12px;margin-bottom:22px;font-size:14px}.ev-brouillon-local button,.ev-lien{color:var(--ev-bleu);background:none;border:0;font-size:14px;padding:8px 0}.ev-retour{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;gap:15px}.ev-retour button{border:0;background:none;color:var(--ev-bleu);padding:9px 0;font-size:14px}.ev-retour>span{color:var(--ev-gris);font-size:12px}.ev-editeur{display:grid;grid-template-columns:245px minmax(0,1fr);background:#fff;border-radius:18px;border:1px solid var(--ev-ligne);box-shadow:0 4px 22px #00000003;overflow:hidden;min-height:640px}.ev-etapes{background:#fcfafd;border-right:1px solid var(--ev-ligne);padding:30px 24px;display:flex;flex-direction:column}.ev-etapes>h2{font-size:22px;margin:22px 0 12px;letter-spacing:-.6px}.ev-etapes>p{font-size:13px;color:var(--ev-gris)}.ev-etapes nav{display:flex;flex-direction:column;gap:5px;margin:28px -8px}.ev-etapes nav button{display:flex;gap:11px;align-items:center;border:0;background:none;text-align:left;padding:12px 8px;border-radius:10px;font-size:13px;color:var(--ev-gris);min-height:46px}.ev-etapes nav button>span{font-size:11px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #e1dae4;border-radius:50%;width:24px;height:24px;color:#6b5a70}.ev-etapes nav button.ev-etape-active{background:#f6f0f9;color:var(--ev-bleu);font-weight:600}.ev-etapes nav button.ev-etape-active>span{border-color:var(--ev-bleu);background:var(--ev-bleu);color:#fff}.ev-rappel{margin-top:auto;border-top:1px solid var(--ev-ligne);padding-top:20px;font-size:11px;color:var(--ev-gris)}.ev-rappel strong{font-weight:600;color:#67566d}.ev-rappel p{margin-top:7px}.ev-formulaire{padding:38px 42px 0;min-width:0;display:flex;flex-direction:column}.ev-formulaire-entete{margin-bottom:30px}.ev-formulaire-entete h2{font-size:26px;letter-spacing:-.8px}.ev-formulaire-entete>p:last-child{color:var(--ev-gris);font-size:14px;margin-top:11px}.ev-section-champs{display:flex;flex-direction:column;gap:24px}.ev-champ{display:flex;flex-direction:column;gap:9px;min-width:0;font-size:13px;font-weight:550}.ev-champ small{font-size:11px;line-height:1.5;color:var(--ev-gris);font-weight:400}.ev-champ input,.ev-champ textarea,.ev-champ select{font-weight:400;font-size:14px}.ev-deux{display:grid;grid-template-columns:1fr 1fr;gap:20px}.ev-types{display:grid;grid-template-columns:1fr 1fr;gap:13px}.ev-types button{display:flex;flex-direction:column;align-items:flex-start;gap:10px;padding:20px;background:#fff;border:1px solid #e1dae4;border-radius:13px;text-align:left;color:var(--ev-encre)}.ev-types button[aria-pressed=true]{border-color:var(--ev-bleu);background:#faf7fc;box-shadow:0 0 0 1px var(--ev-bleu)}.ev-types button>span{font-size:24px;color:var(--ev-bleu)}.ev-types strong{font-size:14px}.ev-types small{color:var(--ev-gris);font-size:11px;line-height:1.45}.ev-note{font-size:12px;color:var(--ev-gris);line-height:1.6!important}.ev-ligne-salle{display:flex;gap:12px;align-items:flex-end;margin-top:14px}.ev-ligne-salle .ev-champ{flex:1}.ev-icone-bouton{height:45px;width:40px;background:none;border:0;font-size:24px;color:var(--ev-gris)}.ev-jours{display:flex;gap:7px;flex-wrap:wrap;margin-top:15px}.ev-jours button{border:1px solid var(--ev-ligne);border-radius:10px;min-height:43px;min-width:49px;background:#fff;font-size:12px;color:var(--ev-gris)}.ev-jours button[aria-pressed=true]{border-color:#c3c4ee;background:#f7f2fa;color:var(--ev-bleu)}.ev-avance{border:1px solid var(--ev-ligne);border-radius:12px;padding:16px;font-size:13px}.ev-avance summary{cursor:pointer;color:var(--ev-gris)}.ev-avance>div{margin-top:20px}.ev-prix{border-top:1px solid var(--ev-ligne);padding-top:25px;display:flex;flex-direction:column;gap:18px}.ev-prix h3{margin-bottom:8px}.ev-choix-prix{display:flex;gap:10px}.ev-choix-prix button{border:1px solid var(--ev-ligne);border-radius:10px;background:#fff;min-height:44px;padding:11px 25px;font-size:13px}.ev-choix-prix button[aria-pressed=true]{border-color:var(--ev-bleu);color:var(--ev-bleu);background:#faf7fc}.ev-parcours-mini{display:flex;gap:6px;overflow:auto;padding-bottom:8px;flex-wrap:wrap}.ev-parcours-mini>span{display:flex;gap:6px;align-items:center;background:#f7f3f9;border-radius:8px;padding:8px;font-size:10px;color:#6b5a70}.ev-parcours-mini strong{font-weight:500;color:#6b5a70;white-space:nowrap}.ev-option{border:1px solid var(--ev-ligne);border-radius:13px;background:#fff;overflow:hidden}.ev-option-fixe{padding:20px;position:relative;background:#fbf9fc}.ev-option-fixe>strong{font-size:14px;font-weight:600}.ev-option-fixe p{font-size:12px;color:var(--ev-gris);margin-top:8px}.ev-option-fixe>span{display:block;font-size:10px;letter-spacing:.4px;text-transform:uppercase;color:#6b5a70;margin-top:12px}.ev-switch{display:flex;align-items:center;gap:15px;padding:20px;cursor:pointer}.ev-switch>span:first-child{flex:1}.ev-switch strong{font-size:14px;font-weight:550}.ev-switch small{display:block;font-size:12px;line-height:1.5;color:var(--ev-gris);margin-top:7px}.ev-switch>input{position:absolute;opacity:0;width:1px!important;height:1px!important;min-height:1px!important}.ev-switch-dessin{width:39px;height:24px;border-radius:14px;background:#dad0dd;position:relative;flex-shrink:0;transition:background .15s}.ev-switch-dessin:after{content:'';display:block;position:absolute;background:#fff;box-shadow:0 1px 3px #0002;border-radius:50%;height:20px;width:20px;top:2px;left:2px;transition:left .15s}.ev-switch>input:checked+.ev-switch-dessin{background:var(--ev-bleu)}.ev-switch>input:checked+.ev-switch-dessin:after{left:17px}.ev-switch>input:focus-visible+.ev-switch-dessin{outline:3px solid #c3c3ed;outline-offset:3px}.ev-option-detail{padding:0 20px 20px}.ev-question{border-top:1px solid var(--ev-ligne);padding-top:18px;margin-bottom:15px}.ev-question-options{display:flex;align-items:center;gap:15px;margin-top:12px;font-size:12px;flex-wrap:wrap}.ev-question-options select{font-size:12px;max-width:180px;padding:8px 10px;min-height:38px}.ev-question-options label{display:flex;align-items:center;gap:7px;font-size:12px}.ev-question-options button{font-size:12px;margin-left:auto}.ev-recap{border:1px solid var(--ev-ligne);border-radius:16px;padding:25px;background:linear-gradient(145deg,#fdfcfe,#fff)}.ev-recap-haut{display:flex;justify-content:space-between;align-items:center;margin-bottom:22px}.ev-recap>h3{font-size:22px;letter-spacing:-.6px;margin-bottom:12px}.ev-recap>p:not(.ev-surtitre){font-size:13px;color:var(--ev-gris)}.ev-recap dl{margin:22px 0 0;padding-top:8px;border-top:1px solid var(--ev-ligne);font-size:13px}.ev-recap dl>div{display:grid;grid-template-columns:95px 1fr;gap:10px;margin-top:14px}.ev-recap dt{color:var(--ev-gris)}.ev-recap dd{margin:0;line-height:1.5}.ev-parcours-recap{display:flex;flex-wrap:wrap;gap:8px;list-style:none;counter-reset:etape;padding:0;margin:15px 0 0}.ev-parcours-recap li{counter-increment:etape;display:flex;align-items:center;gap:8px;padding:9px 11px;border-radius:9px;border:1px solid var(--ev-ligne);font-size:11px}.ev-parcours-recap li:before{content:counter(etape);display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;color:var(--ev-bleu);background:#f7f2fa;font-size:10px}.ev-test{padding:22px;background:#faf7fc;border-radius:13px;display:flex;align-items:flex-start;gap:14px;flex-wrap:wrap}.ev-test>div{width:100%}.ev-test h3{font-size:16px}.ev-test p{font-size:12px;color:var(--ev-gris);margin-top:8px}.ev-test .ev-primaire,.ev-test .ev-secondaire{font-size:12px!important}.ev-formulaire-pied{margin-top:35px;padding:22px 0;border-top:1px solid var(--ev-ligne);display:flex;align-items:center;gap:14px;flex-wrap:wrap}.ev-formulaire-pied>span{flex:1;color:var(--ev-gris);font-size:10px;text-align:center;line-height:1.5;max-width:180px;margin:auto}.ev-actions{display:flex;gap:10px;justify-content:flex-end;align-items:center;flex-wrap:wrap}.ev-dialogue{border:1px solid var(--ev-ligne);border-radius:18px;padding:30px;max-width:520px;width:calc(100% - 32px);color:var(--ev-encre);box-shadow:0 24px 80px #0003}.ev-dialogue::backdrop{background:#2a163255;backdrop-filter:blur(3px)}.ev-dialogue h2{font-size:24px;letter-spacing:-.65px}.ev-dialogue>p{font-size:14px;color:var(--ev-gris);margin:16px 0 22px}.ev-dialogue .ev-actions{margin-top:25px}.ev-accord{display:flex;gap:12px;align-items:flex-start;font-size:13px;line-height:1.6;margin-top:14px;cursor:pointer}.ev-accord input{margin-top:2px}.ev-accord-demo{background:#fff9ed;border:1px solid #f1e4c7;border-radius:11px;padding:15px;color:#725e32;font-size:12px}.ev-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}@media(max-width:1000px){.ev-editeur{grid-template-columns:200px minmax(0,1fr)}.ev-etapes{padding:25px 18px}.ev-formulaire{padding:30px 25px 0}.ev-formulaire-pied>span{display:none}.ev-formulaire-pied{justify-content:space-between}.ev-formulaire-entete h2{font-size:23px}.ev-deux{gap:15px}}@media(max-width:720px){.ev-entete{align-items:flex-start;gap:14px}.ev-entete h2{font-size:23px}.ev-entete p:not(.ev-surtitre){font-size:13px}.ev-barre{align-items:stretch;flex-direction:column;gap:14px}.ev-onglets{align-self:flex-start}.ev-onglets button{font-size:11px;padding:9px 11px}.ev-recherche{max-width:none}.ev-cartes{grid-template-columns:1fr}.ev-carte{min-height:0;padding:21px}.ev-editeur{grid-template-columns:1fr}.ev-etapes{border-right:0;border-bottom:1px solid var(--ev-ligne);padding:14px}.ev-etapes>.ev-pastille,.ev-etapes>h2,.ev-etapes>p,.ev-rappel{display:none}.ev-etapes nav{flex-direction:row;margin:0;gap:4px;justify-content:space-between}.ev-etapes nav button{flex:1;font-size:10px;justify-content:center;flex-direction:column;padding:10px 4px;gap:6px}.ev-etapes nav button>span{width:22px;height:22px}.ev-formulaire{padding:25px 20px 0}.ev-formulaire-entete{margin-bottom:25px}.ev-formulaire-entete h2{font-size:23px}.ev-formulaire-entete>p:last-child{font-size:12px}.ev-section-champs{gap:21px}.ev-deux{grid-template-columns:1fr;gap:19px}.ev-types{gap:10px}.ev-types button{padding:15px 12px}.ev-types strong{font-size:12px}.ev-types small{font-size:10px}.ev-types button>span{font-size:22px}.ev-formulaire-pied>.ev-actions{flex:1}.ev-formulaire-pied>.ev-actions>button{font-size:11px!important;padding:10px 12px}.ev-formulaire-pied>.ev-secondaire{font-size:12px!important;padding:10px 12px}.ev-switch{padding:18px 15px}.ev-switch strong{font-size:13px}.ev-switch small{font-size:11px}.ev-option-detail{padding:0 15px 18px}.ev-recap{padding:20px}.ev-recap dl>div{grid-template-columns:65px 1fr;font-size:12px}.ev-jours{gap:5px}.ev-jours button{min-width:37px;flex:1}.ev-retour>span{font-size:10px}.ev-dialogue{padding:24px}.ev-dialogue .ev-actions>button{flex:1;font-size:12px!important}.ev-vide{padding:30px 20px}.ev-vide h3{font-size:18px}.ev-vide p{font-size:13px}}@media(prefers-reduced-motion:reduce){.ev-root *{transition:none!important;scroll-behavior:auto!important}}
.ev-inventaire-sync{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-bottom:25px;padding-bottom:16px;border-bottom:1px solid #f1eaf4}.ev-inventaire-sync p{font-size:11px;color:#856f8e}.ev-inventaire-sync button{font-size:12px;flex-shrink:0}.ev-article-source{border:1px solid #ece0f3;border-radius:14px;background:#fcfafd;padding:23px}.ev-article-resume{display:flex;align-items:center;justify-content:space-between;gap:16px;margin:17px 0}.ev-article-resume strong{font-size:22px;letter-spacing:-.5px}.ev-article-resume small{font-size:12px;color:#6b5a70;font-weight:400;letter-spacing:0}.ev-article-resume span{display:block;font-size:12px;color:#796681;margin-top:7px}.ev-article-source .ev-note{line-height:1.7}.ev-configuration{grid-template-columns:minmax(0,1fr);overflow:visible}.ev-configuration>.ev-etapes{padding:14px 24px;border-right:0;border-bottom:1px solid var(--ev-ligne);background:#fcfafd;border-radius:17px 17px 0 0}.ev-configuration>.ev-etapes>.ev-pastille,.ev-configuration>.ev-etapes>h2,.ev-configuration>.ev-etapes>p,.ev-configuration>.ev-etapes>.ev-rappel{display:none}.ev-configuration .ev-etapes nav{flex-direction:row;gap:10px;margin:0}.ev-configuration .ev-etapes nav button{justify-content:center;flex:1;font-size:13px;padding:12px 15px}.ev-configuration .ev-formulaire{padding:32px 32px 0}.ev-configuration .ev-formulaire-entete{margin-bottom:28px}.ev-configuration .ev-types{grid-template-columns:repeat(3,minmax(0,1fr))}.ev-demande-libre{border:1px solid #ede4f1;background:#fbf9fd;border-radius:14px;padding:30px;line-height:1.7}.ev-demande-libre p{font-size:14px;color:#75627d;margin:15px 0 20px;max-width:650px}.ev-configuration .ev-formulaire>fieldset{min-width:0}.ev-configuration .ev-formulaire-pied{position:sticky;bottom:0;background:#fffffff5;backdrop-filter:blur(12px);z-index:3;margin-bottom:0}.ev-configuration .ev-formulaire-pied>span{max-width:none;text-align:left}.ev-configuration .ev-section-champs{max-width:900px;margin:auto}.ev-configuration .ev-etapes nav .ev-etape-active{box-shadow:0 2px 8px #391d4410}.ev-configuration .ev-types button{text-align:left;align-items:flex-start}.ev-configuration .ev-types small{font-size:12px;line-height:1.6}.ev-configuration .ev-types strong{font-size:14px}
@media(max-width:1000px){.ev-configuration .ev-formulaire{padding:26px 22px 0}.ev-configuration .ev-etapes nav button{font-size:12px}.ev-configuration .ev-types{grid-template-columns:1fr}.ev-configuration .ev-types button{padding:18px;display:grid;grid-template-columns:36px 1fr;column-gap:15px;row-gap:5px}.ev-configuration .ev-types button>span{grid-row:1/3;margin:0}.ev-configuration .ev-types small{grid-column:2}}@media(max-width:720px){.ev-configuration>.ev-etapes{padding:9px}.ev-configuration .ev-etapes nav{gap:3px}.ev-configuration .ev-etapes nav button{font-size:10px;padding:12px 4px;white-space:nowrap}.ev-configuration .ev-formulaire{padding:23px 14px 0}.ev-configuration .ev-formulaire-pied{gap:10px;padding:15px 0}.ev-configuration .ev-formulaire-pied>.ev-actions{justify-content:flex-end}.ev-configuration .ev-formulaire-pied button{font-size:11px!important}.ev-configuration .ev-formulaire-entete h2{font-size:23px}.ev-demande-libre{padding:20px}}

@media(max-width:720px){.ev-configuration .ev-formulaire-pied>.ev-secondaire{display:none}.ev-configuration .ev-formulaire-pied>.ev-actions{width:100%;flex-wrap:nowrap;justify-content:space-between}.ev-configuration .ev-formulaire-pied>.ev-actions>button{flex:1}}

`
